import { create } from 'zustand';
import React from 'react';
import { CameraService } from '@/services/camera/cameraService';
import { createLogger } from '@/utils/logger';
import { safeStorage } from '@/utils/safeStorage';

const logger = createLogger('CameraStore');

// Persistence keys, one per facing side. Kept here so callers never
// need to know which key to use — the store reads/writes them based
// on the device's side classification.
const STORAGE_REAR_DEVICE = 'pinCam_lastRearDeviceId';
const STORAGE_FRONT_DEVICE = 'pinCam_lastFrontDeviceId';

type Side = 'front' | 'rear' | 'unknown';
type FacingMode = 'user' | 'environment';

/**
 * Minimal shape of the fields we read off a thrown value. Browsers raise
 * `DOMException` / `Error` instances from the camera APIs; this lets us read
 * `message` under strict mode without an `any` cast.
 */
interface ErrorLike {
    message?: string;
}

/** Narrow an unknown thrown value to the field we log on. */
function asErrorLike(error: unknown): ErrorLike {
    if (error && typeof error === 'object') {
        const e = error as { message?: unknown };
        return { message: typeof e.message === 'string' ? e.message : undefined };
    }
    return { message: typeof error === 'string' ? error : undefined };
}

/**
 * Classify a videoinput as front-facing, rear-facing, or unknown
 * based on its label (the only signal MediaDeviceInfo gives us).
 *
 * Mobile browsers (iOS Safari 14+, Chromium Android) reliably tag
 * cameras like "Back Camera" / "Front Camera" / "Back Ultra Wide
 * Camera" once camera permission has been granted. Desktop webcams
 * ("FaceTime HD Camera", "Logitech BRIO") fall into "unknown" — by
 * convention we bucket those into rear since the app's default
 * facingMode is 'environment'.
 */
function classifyDevice(d: MediaDeviceInfo): Side {
    const label = (d.label || '').toLowerCase();
    if (/\b(front|user|selfie|face)\b/.test(label)) return 'front';
    if (/\b(back|rear|environment)\b/.test(label)) return 'rear';
    return 'unknown';
}

function bucketDevices(devices: MediaDeviceInfo[]): {
    rear: MediaDeviceInfo[];
    front: MediaDeviceInfo[];
} {
    const rear: MediaDeviceInfo[] = [];
    const front: MediaDeviceInfo[] = [];
    for (const d of devices) {
        const side = classifyDevice(d);
        if (side === 'front') {
            front.push(d);
        } else {
            // 'rear' + 'unknown' → rear bucket. Mobile devices with
            // labels go in the correct one; desktop webcams (unknown)
            // land here and the lens selector picks them up — fine
            // because facingMode is meaningless on desktop anyway.
            rear.push(d);
        }
    }
    return { rear, front };
}

interface CameraState {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    isReady: boolean;
    isInitializing: boolean;
    cameraError: string | null;
    stream: MediaStream | null;
    facingMode: FacingMode;
    availableDevices: MediaDeviceInfo[];
    rearDevices: MediaDeviceInfo[];
    frontDevices: MediaDeviceInfo[];
    /** Currently active deviceId, or null when running via facingMode
     *  constraint without a specific device pinned. */
    currentDeviceId: string | null;
    initializeCamera: () => Promise<void>;
    startCamera: () => Promise<void>;
    stopCamera: () => void;
    capturePhoto: () => Promise<File | null>;
    flipCamera: () => void;
    selectDevice: (deviceId: string) => Promise<void>;
    loadDevices: () => Promise<void>;
    retry: () => void;
    setVideoRef: (ref: React.RefObject<HTMLVideoElement | null>) => void;
}

const cameraService = new CameraService();
// Exported so diagnostic UI (e.g. BackgroundCamera debug badge) can read
// non-reactive state like the gyro-derived physical angle without adding
// a Zustand slice field that changes every frame.
export { cameraService };

export const useCameraStore = create<CameraState>((set, get) => ({
    videoRef: React.createRef<HTMLVideoElement>(),
    canvasRef: React.createRef<HTMLCanvasElement>(),
    isReady: false,
    isInitializing: false,
    cameraError: null,
    stream: null,
    facingMode: 'environment',
    availableDevices: [],
    rearDevices: [],
    frontDevices: [],
    currentDeviceId: null,

    setVideoRef: (ref) => set({ videoRef: ref }),

    startCamera: async () => {
        // Alias for initializeCamera, kept for naming consistency with
        // older callsites.
        await get().initializeCamera();
    },

    stopCamera: () => {
        cameraService.stop();
        set({ isReady: false, stream: null });
    },

    initializeCamera: async () => {
        const { isReady, isInitializing, videoRef, facingMode } = get();
        if (isInitializing || isReady) {
            logger.info('Initialization already in progress or camera is ready.');
            return;
        }

        logger.info('Initializing camera...');
        set({ isInitializing: true, cameraError: null });

        try {
            if (!videoRef.current) {
                await new Promise<void>((r) => setTimeout(r, 100)); // wait for ref attach
                if (!videoRef.current) {
                    throw new Error('Video element did not become available.');
                }
            }

            // Single getUserMedia for the whole bring-up. This is the ONLY
            // call here that may trigger the OS permission prompt, so the
            // entire flow shows EXACTLY ONE camera prompt.
            //
            // iOS gotcha (the bug this fixes): every getUserMedia issued
            // shows its own camera prompt on iPhone, and iOS allows only one
            // active camera at a time. The old flow did a SECOND getUserMedia
            // afterwards (switchToDevice to restore the persisted lens) —
            // that re-showed the prompt ("asks again") and, with two streams
            // briefly in flight, threw NotReadableError and left the feed
            // dead ("error"). Instead we fold the persisted lens into THIS
            // call as a soft `ideal` deviceId hint: honored when present,
            // silently ignored (→ facingMode default) when the id is stale.
            const storageKey =
                facingMode === 'environment' ? STORAGE_REAR_DEVICE : STORAGE_FRONT_DEVICE;
            const savedId = safeStorage.getItem(storageKey);

            const success = await cameraService.initialize(
                videoRef.current,
                facingMode,
                savedId,
            );
            if (!success) {
                throw new Error('Camera initialization failed');
            }
            const stream = cameraService.getStream();
            const activeDeviceId = cameraService.getActiveDeviceId();
            logger.info('Camera initialized', { hasStream: !!stream, activeDeviceId });
            set({ stream, isReady: true, currentDeviceId: activeDeviceId });

            // Post-init: enumerate devices now that permission is granted and
            // labels are populated (iOS Safari gotcha: labels are empty
            // strings until the user has accepted the prompt at least once).
            // Best-effort, never throws back to the caller.
            await get().loadDevices();

            // If the persisted lens is gone from the freshly enumerated list,
            // drop its key so we stop hinting a device that no longer exists
            // (typical on iOS across sessions). No extra getUserMedia.
            if (savedId && !get().availableDevices.some((d) => d.deviceId === savedId)) {
                safeStorage.removeItem(storageKey);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            logger.error('Camera initialization failed', { error: errorMessage });
            set({ cameraError: errorMessage, isReady: false });
        } finally {
            set({ isInitializing: false });
        }
    },

    capturePhoto: async () => {
        const { isReady } = get();
        if (!isReady) {
            logger.warn('Cannot capture photo: camera not ready');
            return null;
        }
        try {
            // Backup permission request: the camera-init chain may have
            // missed the iOS gesture (e.g. flipCamera, or the prompt got
            // suppressed). The capture-button tap is always a fresh
            // gesture, so we get a second chance to enable the gyro here.
            await cameraService.tryRequestOrientationPermission();

            // Use an offscreen canvas to avoid relying on a mounted canvas element
            const offscreenCanvas = document.createElement('canvas');
            const photoData = await cameraService.capturePhoto(offscreenCanvas);
            // Release the full-resolution capture buffer eagerly — Safari
            // reclaims canvas memory lazily and iPhones run close to the
            // page's memory limit during the capture→annotate→export flow.
            offscreenCanvas.width = 0;
            offscreenCanvas.height = 0;
            if (photoData) {
                const response = await fetch(photoData);
                const blob = await response.blob();
                return new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
            }
            return null;
        } catch {
            set({ cameraError: 'Failed to capture photo.' });
            return null;
        }
    },

    flipCamera: () => {
        const { facingMode } = get();
        const newFacingMode: FacingMode = facingMode === 'user' ? 'environment' : 'user';

        // Stop current camera
        cameraService.stop();

        // Update facing + clear currentDeviceId; the re-init below will
        // try to restore the persisted lens for the new side, falling
        // back to facingMode if none is saved.
        set(() => ({
            isReady: false,
            facingMode: newFacingMode,
            stream: null,
            currentDeviceId: null,
        }));

        // Reinitialize for the new side. initializeCamera reads the
        // appropriate persistence key based on facingMode.
        setTimeout(() => {
            get().initializeCamera();
        }, 100);
    },

    selectDevice: async (deviceId: string) => {
        const { availableDevices } = get();

        // Find the device so we can classify it for persistence.
        const device = availableDevices.find((d) => d.deviceId === deviceId);
        if (!device) {
            logger.warn('selectDevice called with unknown deviceId', { deviceId });
            return;
        }
        const side = classifyDevice(device);

        set({ isReady: false });

        try {
            const ok = await cameraService.switchToDevice(deviceId);
            if (!ok) {
                throw new Error('switchToDevice returned false');
            }

            const stream = cameraService.getStream();

            // Sync facingMode so the rest of the app (flipCamera,
            // visibility rules on CameraScreen) keeps a consistent
            // mental model of which "side" we're on.
            const newFacingMode: FacingMode = side === 'front' ? 'user' : 'environment';

            set({
                stream,
                isReady: true,
                currentDeviceId: deviceId,
                facingMode: newFacingMode,
            });

            // Persist for the correct side. Unknown-side devices land in
            // the rear bucket (see classifyDevice) so they share the
            // rear key — desktop webcams effectively persist as rear.
            const storageKey =
                newFacingMode === 'environment' ? STORAGE_REAR_DEVICE : STORAGE_FRONT_DEVICE;
            safeStorage.setItem(storageKey, deviceId);
        } catch (err: unknown) {
            const message = asErrorLike(err).message;
            logger.error('selectDevice failed', { error: message });
            // Clear the relevant key — the device may have died mid-
            // session. Next init will fall back to facingMode default.
            const storageKey =
                side === 'front' ? STORAGE_FRONT_DEVICE : STORAGE_REAR_DEVICE;
            safeStorage.removeItem(storageKey);
            // switchToDevice stops the current stream before acquiring the new
            // one (iOS single-camera rule), so a failed switch leaves NO live
            // feed. Recover by reopening the facingMode default instead of
            // stranding the user on a dead camera. initializeCamera's guard
            // lets it run now that isReady is false.
            set({ stream: null, isReady: false });
            try {
                await get().initializeCamera();
            } catch {
                /* initializeCamera sets cameraError itself on failure */
            }
            if (!get().isReady) {
                set({ cameraError: `Failed to switch camera: ${message || 'unknown'}` });
            }
        }
    },

    loadDevices: async () => {
        const enumerate = async (): Promise<MediaDeviceInfo[]> => {
            // Cast around the local MediaDeviceInfo interface declared
            // in cameraService.ts (which lacks `toJSON` from lib.dom).
            // At runtime the browser hands us proper DOM MediaDeviceInfo.
            return (await cameraService.getCameraDevices()) as unknown as MediaDeviceInfo[];
        };

        const commit = (devices: MediaDeviceInfo[]) => {
            const { rear, front } = bucketDevices(devices);
            set({
                availableDevices: devices,
                rearDevices: rear,
                frontDevices: front,
            });
            logger.debug('Loaded camera devices', {
                count: devices.length,
                rear: rear.length,
                front: front.length,
                labels: devices.map((d) => d.label).slice(0, 5),
            });
        };

        try {
            const first = await enumerate();
            commit(first);

            // iOS Safari sometimes resolves enumerateDevices() before
            // labels are populated. If the first pass returned an
            // empty list, OR returned entries with empty labels (so
            // the classifier can't tell front from rear), retry once
            // after a short delay. Belt-and-suspenders for the worst
            // platforms; a no-op on browsers that get it right the
            // first time.
            const labelsEmpty = first.every((d) => !d.label);
            if (first.length === 0 || labelsEmpty) {
                setTimeout(async () => {
                    try {
                        const second = await enumerate();
                        if (second.length > first.length || second.some((d) => d.label)) {
                            commit(second);
                        }
                    } catch {
                        /* swallow — primary load already committed */
                    }
                }, 500);
            }
        } catch (err: unknown) {
            logger.warn('Failed to enumerate devices', { error: asErrorLike(err).message });
            // Non-fatal — keep existing arrays so UI doesn't flicker.
        }
    },

    retry: () => {
        get().initializeCamera();
    },
}));
